// src/pages/InDevelopment.js
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Hammer, ArrowLeft, Sparkles } from "lucide-react";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const FEATURE_COPY = {
  "watch-parties": {
    title: "Watch Parties",
    desc: "Live rooms, synced playback, chat, host tools, and scheduling.",
  },
  "fan-art": {
    title: "Fan Art Hub",
    desc: "Artist profiles, uploads, tags, galleries, and spotlight features.",
  },
  "recommendations": {
    title: "Recommendations",
    desc: "Ask for recs, curated lists, and personalized suggestions.",
  },
  "clips": {
    title: "Clips / AMV",
    desc: "Upload clips, like/comment, and discover trending edits.",
  },
  "recs": {
    title: "Recs & Lists",
    desc: "Community lists, favorites, and ranking boards.",
  },
};

export default function InDevelopment() {
  const navigate = useNavigate();
  const query = useQuery();

  const feature = (query.get("feature") || "").toLowerCase();
  const pretty = FEATURE_COPY[feature] || {
    title: "This Feature",
    desc: "We’re actively building it and polishing the experience.",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-gray-900 to-black text-white">
      <div className="w-full px-4 sm:px-6 pt-10 pb-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full bg-gray-900/80 border border-white/10 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800/80 hover:border-emerald-500/30 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-4 py-2 text-sm text-emerald-200 hover:bg-emerald-500/20 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              Home
            </button>
          </div>

          {/* Card */}
          <div className="rounded-3xl bg-gray-950/80 border border-emerald-500/20 shadow-[0_0_24px_rgba(16,185,129,0.08)] overflow-hidden">
            <div className="p-5 sm:p-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                  <Hammer className="w-5 h-5 text-emerald-300" />
                </div>
                <div>
                  <p className="uppercase tracking-[0.18em] text-[10px] sm:text-xs text-emerald-300/80">
                    AnimeHouse · In Development
                  </p>
                  <h1 className="text-xl sm:text-2xl font-bold text-emerald-300">
                    {pretty.title} is coming soon
                  </h1>
                </div>
              </div>

              <p className="text-gray-200 leading-relaxed text-sm sm:text-base">
                {pretty.desc} This page isn’t live yet — but it’s on the build
                list and we’re working on it.
              </p>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="w-full rounded-2xl bg-gray-900/90 hover:bg-gray-800 text-white px-4 py-3 text-sm font-semibold border border-white/10 transition-all"
                >
                  Go back
                </button>

                <button
                  onClick={() => navigate("/")}
                  className="w-full rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-3 text-sm font-semibold transition-all"
                >
                  Return to Home
                </button>
              </div>

              <div className="mt-6 rounded-2xl bg-gray-900/70 border border-white/10 p-4">
                <p className="text-xs text-gray-300">
                  Tip: We route unfinished links here so the site never feels
                  broken. 
                </p>
                <p className="text-[11px] text-gray-500 mt-1">
                  Feature key:{" "}
                  <span className="text-emerald-300 font-semibold">
                    {feature || "none"}
                  </span>
                </p>
              </div>
            </div>

            <div className="px-5 sm:px-7 py-4 border-t border-white/5 text-[11px] text-gray-500">
              © {new Date().getFullYear()} AnimeHouse — building in public.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
